/**
 * The admin panel's page registry — the ONE list the sidebar, the route gate
 * and the staff form's "page access" tree are built from.
 *
 * Keys are what `user.pagePermissions` stores: a section key ("orders") allows
 * every page in the section; a page key ("orders.live") allows that page. The
 * role still decides the ceiling — a page a role cannot see is not offered in
 * the tree for that role, and the API is authorised by role regardless.
 *
 * `paths` are route prefixes. The first registry entry whose prefix matches the
 * current pathname (longest prefix wins) is the page being viewed; a path with
 * no entry (the dashboard, redirects) is never gated.
 */

export interface AdminPage {
  /** "section.page" */
  key: string;
  label: string;
  /** Route prefixes that belong to this page (longest match wins). */
  paths: string[];
}

export interface AdminSection {
  key: string;
  label: string;
  pages: AdminPage[];
}

export const ADMIN_SECTIONS: AdminSection[] = [
  {
    key: 'orders', label: 'Orders & Operations',
    pages: [
      { key: 'orders.live', label: 'Live Orders', paths: ['/live-orders'] },
      { key: 'orders.list', label: 'Orders', paths: ['/orders'] },
      { key: 'orders.uberDeliveries', label: 'Uber Deliveries', paths: ['/uber-deliveries'] },
      { key: 'orders.tfsDeliveries', label: 'TFS Deliveries', paths: ['/tfs-deliveries'] },
    ],
  },
  {
    key: 'uberEats', label: 'Uber Eats',
    pages: [
      { key: 'uberEats.orders', label: 'Uber Orders', paths: ['/uber-orders'] },
      { key: 'uberEats.menu', label: 'Uber Menu', paths: ['/uber-menu'] },
      { key: 'uberEats.promotions', label: 'Uber Promotions', paths: ['/uber-promotions'] },
      { key: 'uberEats.tools', label: 'Uber Tools', paths: ['/uber-tools'] },
    ],
  },
  {
    key: 'customers', label: 'Customers',
    pages: [
      { key: 'customers.list', label: 'Customers', paths: ['/customers'] },
    ],
  },
  {
    key: 'products', label: 'Products',
    pages: [
      { key: 'products.staging', label: 'Deeliver Item Staging', paths: ['/deeliver/staging'] },
      { key: 'products.management', label: 'Deeliver Products', paths: ['/products'] },
      { key: 'products.sync', label: 'Deeliver Sync', paths: ['/deeliver/sync'] },
    ],
  },
  {
    key: 'config', label: 'Configuration',
    pages: [
      { key: 'config.content', label: 'Content Control', paths: ['/content/control'] },
      { key: 'config.commandCenter', label: 'Store Command Center', paths: ['/stores'] },
      { key: 'config.webhookEndpoints', label: 'Webhook Endpoints', paths: ['/webhook-endpoints'] },
    ],
  },
  {
    key: 'vaperite', label: 'Vaperite',
    pages: [
      { key: 'vaperite.staging', label: 'Vaperite Item Staging', paths: ['/vaperite/staging'] },
      { key: 'vaperite.products', label: 'Vaperite Products', paths: ['/vaperite/products'] },
      { key: 'vaperite.stores', label: 'Vaperite Stores', paths: ['/vaperite/stores'] },
      { key: 'vaperite.orderTablets', label: 'Vaperite Order Tablets', paths: ['/vaperite/order-tablets', '/admin-functions/order-manager-tablets'] },
      { key: 'vaperite.sync', label: 'Vaperite Sync', paths: ['/vaperite/sync'] },
    ],
  },
  {
    key: 'marketing', label: 'Marketing',
    pages: [
      { key: 'marketing.messages', label: 'Store Messages', paths: ['/messages'] },
      // Off the rail (2026-09-14) but still routed — listed so it can be refused.
      { key: 'marketing.seo', label: 'Marketing & SEO', paths: ['/marketing/seo'] },
    ],
  },
  {
    // shown in the sidebar under MARKETING since 26 Oct 2026; the keys stay discounts.* for stored grants
    key: 'discounts', label: 'Marketing: discounts',
    pages: [
      { key: 'discounts.promotions', label: 'Promotions', paths: ['/discounts/promotions'] },
      { key: 'discounts.customers', label: 'Customers', paths: ['/discounts/customers'] },
      { key: 'discounts.loyalty', label: 'Loyalty', paths: ['/discounts/loyalty'] },
      { key: 'discounts.referrals', label: 'Referrals', paths: ['/discounts/referrals'] },
    ],
  },
  {
    key: 'adminTools', label: 'Admin Tools',
    pages: [
      { key: 'adminTools.staff', label: 'Staff Users', paths: ['/admin/staff'] },
      { key: 'adminTools.inventoryTools', label: 'Inventory Tools', paths: ['/tools'] },
      { key: 'adminTools.missions', label: 'Mission Builder', paths: ['/missions'] },
      { key: 'adminTools.customerMap', label: 'Customer Map', paths: ['/customer-map'] },
      { key: 'adminTools.metrics', label: 'System Parameters', paths: ['/admin/metrics'] },
      { key: 'adminTools.systemCore', label: 'System Core', paths: ['/settings/system-core'] },
      { key: 'adminTools.webhookDispatches', label: 'Webhook Dispatches', paths: ['/webhook-dispatches'] },
      { key: 'adminTools.syncControl', label: 'Sync Control', paths: ['/admin-functions/sync-control'] },
      { key: 'adminTools.webhookTesting', label: 'Webhook Testing', paths: ['/integrations/webhook-testing'] },
      { key: 'adminTools.devMenu', label: 'Dev Menu', paths: ['/admin-functions/dev-menu'] },
    ],
  },
  {
    // Not pages: the home dashboard's elements, ticked one by one (Jason, 31 Oct 2026). No
    // paths, so they never gate a route; the Dashboard shows a staff user only the ticked ones,
    // with order figures limited to their assigned stores.
    key: 'dashboard', label: 'Dashboard',
    pages: [
      { key: 'dashboard.liveUsers', label: 'Live Users', paths: [] },
      { key: 'dashboard.liveOrders', label: 'Live Orders', paths: [] },
      { key: 'dashboard.livePages', label: 'Live Pages', paths: [] },
      { key: 'dashboard.customers', label: 'Deeliver Customers', paths: [] },
      { key: 'dashboard.orders', label: 'Deeliver Orders', paths: [] },
      { key: 'dashboard.revenue', label: 'Revenue', paths: [] },
      { key: 'dashboard.avgCart', label: 'Average Cart', paths: [] },
      { key: 'dashboard.avgDeliveryTime', label: 'Average Delivery Time', paths: [] },
      { key: 'dashboard.stores', label: 'Deeliver Stores', paths: [] },
      { key: 'dashboard.newCustomers', label: 'New Customers', paths: [] },
      { key: 'dashboard.monthToDate', label: 'This month so far', paths: [] },
      { key: 'dashboard.jumpTo', label: 'Jump to', paths: [] },
    ],
  },
];

const ALL_PAGES: AdminPage[] = ADMIN_SECTIONS.flatMap((s) => s.pages);

/** The page a pathname belongs to, or null when it is not a gated page. */
export const pageKeyForPath = (pathname: string): string | null => {
  let best: { key: string; len: number } | null = null;
  for (const page of ALL_PAGES) {
    for (const prefix of page.paths) {
      const matches = pathname === prefix || pathname.startsWith(`${prefix}/`);
      if (matches && (!best || prefix.length > best.len)) best = { key: page.key, len: prefix.length };
    }
  }
  return best?.key ?? null;
};

/** The page key for a sidebar row's destination — same rule as the gate. */
export const pageKeyForNav = pageKeyForPath;

export const sectionOf = (pageKey: string): string => pageKey.split('.')[0];

/**
 * Does an allow-list admit a page? null/empty = role default = everything the
 * role shows. A section key admits all its pages.
 */
/**
 * Page keys renamed when a page moved section. A user granted the OLD page key keeps
 * the page; one granted only the old SECTION does not — Webhook Endpoints and Staff
 * Users are company-wide pages and left the Vaperite section on purpose (2026-10-01).
 */
const LEGACY_PAGE_KEYS: Record<string, string> = {
  'config.webhookEndpoints': 'vaperite.webhookEndpoints',
  'adminTools.staff': 'vaperite.staff',
};

export const allowsPage = (allowList: string[] | null | undefined, pageKey: string): boolean => {
  if (!allowList || allowList.length === 0) return true;
  const legacy = LEGACY_PAGE_KEYS[pageKey];
  return allowList.includes(pageKey) || allowList.includes(sectionOf(pageKey)) || (!!legacy && allowList.includes(legacy));
};

/** Is any page of a section admitted (so its header is worth showing)? */
export const allowsSection = (allowList: string[] | null | undefined, sectionKey: string): boolean => {
  if (!allowList || allowList.length === 0) return true;
  if (allowList.includes(sectionKey)) return true;
  return allowList.some((k) => sectionOf(k) === sectionKey);
};

/**
 * Staff see and use exactly what is ticked (Jason, 31 Oct 2026): no ticks = no pages, and the
 * API allows a staff call only for a ticked page. Owner and Admin keep the old rule: no list =
 * everything, a list narrows it.
 */
export const isTicksOnlyRole = (role: string | null | undefined): boolean => role === 'staff';

const hasTicks = (role: string | null | undefined, allowList: string[] | null | undefined) =>
  !isTicksOnlyRole(role) || (!!allowList && allowList.length > 0);

export const allowsPageFor = (role: string | null | undefined, allowList: string[] | null | undefined, pageKey: string): boolean =>
  hasTicks(role, allowList) && allowsPage(allowList, pageKey);

export const allowsSectionFor = (role: string | null | undefined, allowList: string[] | null | undefined, sectionKey: string): boolean =>
  hasTicks(role, allowList) && allowsSection(allowList, sectionKey);

/**
 * Order-tablet access: a tick in the same list, beside the admin pages, but not
 * an admin page. The backend enforces it on every /companion route (the page
 * ticks are the admin's to enforce); the same key, 'tablet', on both sides.
 */
export const TABLET_ACCESS_KEY = 'tablet';

/** Does an access list admit the order tablet? No list = role default = yes. */
export const allowsTablet = (allowList: string[] | null | undefined): boolean =>
  !allowList || allowList.length === 0 || allowList.includes(TABLET_ACCESS_KEY);

/**
 * Does an access list admit any of the admin? A list holding only the Tablet
 * tick is a tablet-only account — the admin refuses it rather than showing an
 * empty dashboard.
 */
export const allowsAdmin = (allowList: string[] | null | undefined): boolean =>
  !allowList || allowList.length === 0 || allowList.some((k) => k !== TABLET_ACCESS_KEY);
